export const BASE_WEIGHT_UNIT = 'g'

export const FAT_CALORIES_PER_GRAM = 9
export const PROTEIN_CALORIES_PER_GRAM = 4
export const CARBOHYDRATES_CALORIES_PER_GRAM = 4

export enum ENutritionalParameterType {
	PRIMARY = 'primary',
	SECONDARY = 'secondary',
	PRICE = 'price',
}

export enum ENutritionalParameters {
	WASTE = 1,
	ENERGY_KJ = 2,
	ENERGY_KCAL = 3,
	ENERGY_KJ_EXTENDED = 4,
	ENERGY_KCAL_EXTENDED = 5,
	WATER = 6,
	PROTEIN_TOTAL = 7,
	PROTEIN_ANIMAL = 8,
	PROTEIN_PLANT = 9,
	PROTEIN_EXTENDED = 10,
	FAT = 11,
	CARBOHYDRATES_TOTAL = 12,
	CARBOHYDRATES_AVAILABLE = 13,
	ASH = 14,
	SODIUM = 15,
	POTASSIUM = 17,
	CALCIUM = 18,
	PHOSPHORUS = 19,
	MAGNESIUM = 20,
	IRON = 21,
	ZINC = 22,
	COPPER = 23,
	MANGANESE = 24,
	IODINE = 25,
	VITAMIN_A = 26,
	RETINOL = 27,
	BETA_CAROTENE = 28,
	VITAMIN_D = 29,
	VITAMIN_E = 30,
	THIAMINE = 31,
	RIBOFLAVIN = 32,
	NIACIN = 33,
	VITAMIN_B6 = 34,
	FOLATES = 35,
	VITAMIN_B12 = 36,
	VITAMIN_C = 37,
	FIBER = 93,
	SIMPLE_SUGAR = 100,
	SALT = 102,
	IG = 104,
	PRICE_NET = 105,
	PRICE_TAX_RATE = 106,
	PRICE_GROSS = 108,
}

const MACRONUTRITIONAL_PARAMETERS = [
	{ id: ENutritionalParameters.PROTEIN_TOTAL, caloriesPerGram: PROTEIN_CALORIES_PER_GRAM },
	{ id: ENutritionalParameters.FAT, caloriesPerGram: FAT_CALORIES_PER_GRAM },
	{ id: ENutritionalParameters.CARBOHYDRATES_TOTAL, caloriesPerGram: CARBOHYDRATES_CALORIES_PER_GRAM },
] as const

export const calculateMacronutritionalPercentages = (
	nutritionals: readonly { id: number; value: number }[],
): Partial<Record<ENutritionalParameters, number>> => {
	const values = MACRONUTRITIONAL_PARAMETERS.map(({ id, caloriesPerGram }) => {
		const value = nutritionals.find(nutritional => nutritional.id === id)?.value ?? 0
		return Number.isFinite(value) && value > 0 ? value * caloriesPerGram : 0
	})
	const total = values.reduce((sum, value) => sum + value, 0)
	const result: Partial<Record<ENutritionalParameters, number>> = {}

	if (!total) {
		for (const { id } of MACRONUTRITIONAL_PARAMETERS) result[id] = 0
		return result
	}

	const exactPercentages = values.map(value => (value / total) * 100)
	const percentages = exactPercentages.map(Math.floor)
	const remainder = 100 - percentages.reduce((sum, value) => sum + value, 0)
	const remainderOrder = exactPercentages
		.map((value, index) => ({ index, remainder: value - percentages[index] }))
		.sort((a, b) => b.remainder - a.remainder || a.index - b.index)

	for (let index = 0; index < remainder; index++) percentages[remainderOrder[index].index]++
	for (const [index, { id }] of MACRONUTRITIONAL_PARAMETERS.entries()) result[id] = percentages[index]

	return result
}

export const DEFAULT_NUTRITIONALS_TO_DISPLAY = [
	ENutritionalParameters.ENERGY_KCAL,
	ENutritionalParameters.PROTEIN_TOTAL,
	ENutritionalParameters.FAT,
	ENutritionalParameters.CARBOHYDRATES_TOTAL,
]

export const PRICE_NUTRITIONALS = [
	ENutritionalParameters.PRICE_NET,
	ENutritionalParameters.PRICE_TAX_RATE,
	ENutritionalParameters.PRICE_GROSS,
]

/**
 * Parametry cenowe skalowane wagą surowca sprzed WOM (weight_before_wom),
 * spójnie z backendowym NutritionalParameterService::resolveComponentWeightForAggregation.
 */
export const WEIGHT_BEFORE_WOM_PRICED_NUTRITIONALS = [
	ENutritionalParameters.PRICE_NET,
	ENutritionalParameters.PRICE_GROSS,
]

export const NUTRITIONALS_ICONS = {
	[ENutritionalParameters.ENERGY_KJ]: 'calories',
	[ENutritionalParameters.ENERGY_KCAL]: 'calories',
	[ENutritionalParameters.PROTEIN_TOTAL]: 'protein',
	[ENutritionalParameters.FAT]: 'fat',
	[ENutritionalParameters.CARBOHYDRATES_TOTAL]: 'carbs',
	[ENutritionalParameters.PRICE_GROSS]: 'food',
}

export const NUTRITIONALS_COLUMNS_DETAILS = [
	{
		id: ENutritionalParameters.PROTEIN_TOTAL,
		field: 'Białko',
		color: '#53C8D5',
	},
	{
		id: ENutritionalParameters.FAT,
		field: 'Tłuszcz',
		color: '#FF855D',
	},
	{
		id: ENutritionalParameters.CARBOHYDRATES_TOTAL,
		field: 'Węglowodany',
		color: '#4BB178',
	},
]

export const NUTRITIONALS_COLUMNS_WIDTH = [
	{
		id: ENutritionalParameters.PRICE_GROSS,
		width: 120,
	},
]
